import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { useUserProperties } from '../hooks/useUserProperties';
import TagsInput from '../../ui/TagsInput';
import { deriveAvailableTags } from '../../../utils/tags';

/**
 * Desktop modal for adding a new lead.
 * @param {Object} props
 * @param {function} props.onClose - Close modal callback
 * @param {function} props.onAddLead - Add lead callback
 * @param {Array} props.leads - All leads for extracting existing tags
 */
export const AddLeadModal = ({ onClose, onAddLead, leads = [] }) => {
    const { properties, loading: propertiesLoading } = useUserProperties();

    // Derive unique normalized tags from all existing leads
    const availableTags = useMemo(() => deriveAvailableTags(leads), [leads]);

    const [selectedTags, setSelectedTags] = useState([]);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        status: 'Active',
        interestedPropertyIds: [], // Multiple property IDs
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleToggleProperty = (propertyId) => {
        setFormData((prev) => {
            const ids = prev.interestedPropertyIds;
            if (ids.includes(propertyId)) {
                return { ...prev, interestedPropertyIds: ids.filter(id => id !== propertyId) };
            }
            return { ...prev, interestedPropertyIds: [...ids, propertyId] };
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Build interested properties array with id and address
        const interestedProperties = formData.interestedPropertyIds
            .map(id => {
                const prop = properties.find(p => p.id === id);
                return prop ? { id: prop.id, address: prop.address || prop.displayName } : null;
            })
            .filter(Boolean);

        // Pass firstName and lastName separately - service will generate fullName
        onAddLead({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            status: formData.status,
            interestedProperties,
            tags: selectedTags,
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-stone-900/20 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white w-[400px] rounded-xl shadow-popover border border-notion-border p-6 fade-in">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="font-semibold text-lg text-notion-text">Add New Lead</h3>
                    <button onClick={onClose} className="text-stone-400 hover:text-stone-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="flex gap-2">
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            placeholder="First Name"
                            className="w-1/2 px-3 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded text-xs outline-none transition"
                            required
                        />
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Last Name"
                            className="w-1/2 px-3 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded text-xs outline-none transition"
                            required
                        />
                    </div>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email Address"
                        className="w-full px-3 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded text-xs outline-none transition"
                        required
                    />
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone Number"
                        className="w-full px-3 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded text-xs outline-none transition"
                        required
                    />
                    <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Address (Optional)"
                        className="w-full px-3 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded text-xs outline-none transition"
                    />
                    <div>
                        <label className="block text-[10px] font-medium text-stone-500 mb-1">Interested Properties (Optional)</label>
                        <div className="max-h-32 overflow-y-auto border border-stone-200 rounded bg-notion-sidebar divide-y divide-stone-100">
                            {propertiesLoading ? (
                                <div className="p-2 text-xs text-stone-400">Loading...</div>
                            ) : properties.length === 0 ? (
                                <div className="p-2 text-xs text-stone-400 italic">No properties available</div>
                            ) : (
                                properties.map((property) => (
                                    <label
                                        key={property.id}
                                        className="flex items-center gap-2 px-3 py-2 hover:bg-white cursor-pointer transition"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={formData.interestedPropertyIds.includes(property.id)}
                                            onChange={() => handleToggleProperty(property.id)}
                                            className="w-4 h-4 rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                                        />
                                        <span className="text-xs text-stone-700">{property.displayName}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                    {/* Tags */}
                    <div>
                        <label className="block text-[10px] font-medium text-stone-500 mb-1">Tags (Optional)</label>
                        <TagsInput
                            tags={selectedTags}
                            onChange={setSelectedTags}
                            suggestions={availableTags}
                            placeholder="Add tags..."
                            size="sm"
                        />
                    </div>
                    <div>
                        <div className="w-1/2">
                            <label className="block text-[10px] font-medium text-stone-500 mb-1">Status</label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 bg-notion-sidebar border border-transparent focus:bg-white focus:border-stone-300 rounded text-xs outline-none transition text-stone-600"
                            >
                                <option>Active</option>
                                <option>Closed</option>
                                <option>Archived</option>
                                <option>Cold</option>
                            </select>
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition">
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-stone-800 text-white text-xs font-medium rounded hover:bg-stone-700 transition"
                        >
                            Create
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddLeadModal;

